// src/business/pages/BusinessEditProduct.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Swal from "../../utils/apparifySwal";
import { ArrowLeft, Loader2, Trash2 } from "lucide-react";

import Panel from "../components/Panel";
import { businessSwalBase } from "../utils/businessSwal";
import { businessProductsAPI } from "../services/businessProductsService";

import StepBasicInfo from "../components/product/steps/StepBasicInfoCatalog";
import StepOptions from "../components/product/steps/StepOptions";
import StepVariants from "../components/product/steps/StepVariants";
import StepInventory from "../components/product/steps/StepInventory";
import StepMedia from "../components/product/steps/StepMediaAssets";
import Step3DAssetsViewer from "../components/product/steps/Step3DAssetsViewer";
import WizardHeader from "../components/product/WizardHeader";
import { buildOptionCombinations, makeVariantKey } from "../components/product/utils/variantMatrix";

/* ────────────────────── constants ────────────────────── */

const STEPS = [
  { key: "basic", label: "Basic Info" },
  { key: "options", label: "Options" },
  { key: "variants", label: "Variants" },
  { key: "inventory", label: "Inventory" },
  { key: "media", label: "Media" },
  { key: "threeD", label: "3D Assets & Viewer" },
];
const STEP_KEYS = STEPS.map((s) => String(s.key || "").toLowerCase());

/* ────────────────────── helpers ────────────────────── */

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function stepIndexFromParam(p) {
  const idx = STEP_KEYS.indexOf(String(p || "").toLowerCase().trim());
  return idx >= 0 ? idx : 0;
}

/** cents → peso string  */
function fromCents(c) {
  const n = Number(c || 0);
  return (n / 100).toFixed(2);
}

/** Display-friendly relative timestamp */
function relativeTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" });
}

function makeOptionId(seed = "opt") {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${seed}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function variantFingerprint(v) {
  const ov = Object.entries(v?.optionValues || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, val]) => `${k}:${val}`)
    .join("|");
  return ov || v?.sku || "";
}

/** Compute option combos that exist in options but not in variants. */
function computeMissingCombos(options, variants) {
  const combos = buildOptionCombinations(options || []);
  const existingKeys = new Set((variants || []).map((v) => makeVariantKey(v?.optionValues || {})));
  return combos.filter((combo) => !existingKeys.has(makeVariantKey(combo)));
}

function buildSaveSummary(prevPayload, nextPayload) {
  if (!prevPayload || !nextPayload) return [];

  const lines = [];

  /* ── Product-level fields ── */

  if ((prevPayload.basic?.name || "") !== (nextPayload.basic?.name || "")) {
    lines.push(`Name: "${prevPayload.basic?.name || "(empty)"}" → "${nextPayload.basic?.name || "(empty)"}"`);
  }

  if ((prevPayload.basic?.description || "") !== (nextPayload.basic?.description || "")) {
    const prev = (prevPayload.basic?.description || "").trim();
    const next = (nextPayload.basic?.description || "").trim();
    if (prev && next) {
      const preview = next.length > 60 ? next.slice(0, 60) + "…" : next;
      lines.push(`Description updated: "${preview}"`);
    } else if (!prev && next) {
      lines.push("Description added.");
    } else {
      lines.push("Description removed.");
    }
  }

  if ((prevPayload.basic?.category || "") !== (nextPayload.basic?.category || "")) {
    lines.push("Category changed.");
  }

  const prevTags = Array.isArray(prevPayload.basic?.tags) ? prevPayload.basic.tags.map(String).sort() : [];
  const nextTags = Array.isArray(nextPayload.basic?.tags) ? nextPayload.basic.tags.map(String).sort() : [];
  if (prevTags.length !== nextTags.length || prevTags.some((tagId, index) => tagId !== nextTags[index])) {
    lines.push("Tags updated.");
  }

  if ((prevPayload.basic?.skuBase || "") !== (nextPayload.basic?.skuBase || "")) {
    lines.push(`SKU base: "${prevPayload.basic?.skuBase || "(auto)"}" → "${nextPayload.basic?.skuBase || "(auto)"}"`);
  }

  /* ── Options — semantic per-option diff ── */

  const prevOpts = prevPayload.options || [];
  const nextOpts = nextPayload.options || [];

  // Build maps by option name (lowercase) for matching
  const prevByName = new Map(prevOpts.map((o, i) => [o.name.toLowerCase(), { ...o, _pos: i }]));
  const nextByName = new Map(nextOpts.map((o, i) => [o.name.toLowerCase(), { ...o, _pos: i }]));

  // Added options
  for (const [key, o] of nextByName) {
    if (!prevByName.has(key)) {
      lines.push(`+ Option added: <b>${o.name}</b> (${o.values.join(", ")})`);
    }
  }

  // Removed options
  for (const [key, o] of prevByName) {
    if (!nextByName.has(key)) {
      lines.push(`− Option removed: <b>${o.name}</b>`);
    }
  }

  // Modified options (same name, different values or position)
  for (const [key, prev] of prevByName) {
    const next = nextByName.get(key);
    if (!next) continue;

    const prevVals = prev.values;
    const nextVals = next.values;
    const prevSet = new Set(prevVals);
    const nextSet = new Set(nextVals);

    const added = nextVals.filter((v) => !prevSet.has(v));
    const removed = prevVals.filter((v) => !nextSet.has(v));

    if (added.length) {
      lines.push(`<b>${next.name}</b>: added ${added.map((v) => `"${v}"`).join(", ")}`);
    }
    if (removed.length) {
      lines.push(`<b>${next.name}</b>: removed ${removed.map((v) => `"${v}"`).join(", ")}`);
    }
  }

  // Option reorder detection (same set of names, different order)
  if (prevOpts.length === nextOpts.length && prevOpts.length > 1) {
    const prevNames = prevOpts.map((o) => o.name.toLowerCase());
    const nextNames = nextOpts.map((o) => o.name.toLowerCase());
    const sameSet = prevNames.length === nextNames.length && prevNames.every((n) => nextNames.includes(n));
    if (sameSet && prevNames.some((n, i) => n !== nextNames[i])) {
      lines.push(`Option order changed: ${nextOpts.map((o) => o.name).join(" → ")}`);
    }
  }

  /* ── Variants ── */

  const prevVariants = new Map((prevPayload.variants || []).map((v) => [variantFingerprint(v), v]));
  const nextVariants = new Map((nextPayload.variants || []).map((v) => [variantFingerprint(v), v]));

  const variantAdded = [...nextVariants.keys()].filter((k) => !prevVariants.has(k)).length;
  const variantRemoved = [...prevVariants.keys()].filter((k) => !nextVariants.has(k)).length;

  const variantEdits = [];
  nextVariants.forEach((v, k) => {
    const p = prevVariants.get(k);
    if (!p) return;
    const changes = [];
    if ((p.sku || "") !== (v.sku || "")) changes.push("SKU");
    if (String(p.price ?? "") !== String(v.price ?? "")) changes.push("price");
    if (!!p.isActive !== !!v.isActive) changes.push(v.isActive ? "activated" : "deactivated");
    if (changes.length) variantEdits.push({ title: Object.values(v.optionValues).join(" / "), changes });
  });

  if (variantAdded) lines.push(`+ ${variantAdded} new variant${variantAdded > 1 ? "s" : ""} added`);
  if (variantRemoved) lines.push(`− ${variantRemoved} variant${variantRemoved > 1 ? "s" : ""} removed`);

  if (variantEdits.length <= 5) {
    for (const ve of variantEdits) {
      lines.push(`Variant "${ve.title}": ${ve.changes.join(", ")} changed`);
    }
  } else {
    lines.push(`${variantEdits.length} variants edited (SKU/price/active)`);
  }

  /* ── Inventory settings ── */

  const pi = prevPayload.inventory || {};
  const ni = nextPayload.inventory || {};
  if (String(pi.lowStockThreshold ?? "") !== String(ni.lowStockThreshold ?? "")) {
    lines.push(`Low stock threshold: ${pi.lowStockThreshold || "(default)"} → ${ni.lowStockThreshold || "(default)"}`);
  }

  /* ── Media ── */

  const prevImages = (prevPayload.media?.productImages || []).length;
  const nextImages = (nextPayload.media?.productImages || []).length;
  if (prevImages !== nextImages) {
    lines.push(`Images: ${prevImages} → ${nextImages}`);
  } else if (
    JSON.stringify(prevPayload.media?.productImages || []) !==
    JSON.stringify(nextPayload.media?.productImages || [])
  ) {
    lines.push("Image order or content changed.");
  }

  const prevVariantImages = (prevPayload.variants || []).map((variant) => ({
    key: variantFingerprint(variant),
    imageUrl: variant?.imageUrl || null,
  }));
  const nextVariantImages = (nextPayload.variants || []).map((variant) => ({
    key: variantFingerprint(variant),
    imageUrl: variant?.imageUrl || null,
  }));
  if (JSON.stringify(prevVariantImages) !== JSON.stringify(nextVariantImages)) {
    lines.push("Variant image assignments updated.");
  }

  return lines;
}

/* ────── status badge ────── */

const STATUS_STYLES = {
  LISTED:
    "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
  DRAFT:
    "border-neutral-300 bg-neutral-100 text-neutral-600 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
  INACTIVE:
    "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
  ARCHIVED:
    "border-neutral-400 bg-neutral-200 text-neutral-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-400",
};

function StatusBadge({ status }) {
  const s = (status || "DRAFT").toUpperCase();
  const label =
    s === "LISTED"
      ? "Listed"
      : s.charAt(0) + s.slice(1).toLowerCase();
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[s] || STATUS_STYLES.DRAFT}`}
    >
      {label}
    </span>
  );
}

/* ────── convert DB product → wizard state ────── */

export function productToState(prod) {
  if (!prod) return null;

  // Parse metadata (JSON string or object)
  let meta = {};
  try {
    meta = typeof prod.metadata === "string" ? JSON.parse(prod.metadata) : prod.metadata || {};
  } catch { /* ignore */ }

  const basic = {
    name: prod.name || "",
    category: prod.primary_category_id ? String(prod.primary_category_id) : "",
    tags: Array.isArray(prod.tags) ? prod.tags.map((tag) => String(tag.id)) : [],
    description: prod.description || "",
    skuBase: meta?.skuBase || "",
  };

  // Options: [{id, name, position, values: [{id, value, position}]}]
  const options = (prod.options || []).map((o) => ({
    id: o.id || makeOptionId("opt"),
    name: o.name || "",
    values: (o.values || []).map((v) => v.value || ""),
  }));

  // Variants
  const variants = (prod.variants || []).map((v) => ({
    id: v.id || null,
    optionValues: v.optionValues || {},
    sku: v.sku || "",
    imageUrl: v.imageUrl || null,
    isActive: v.active !== 0,
    price: v.price_override_cents != null ? fromCents(v.price_override_cents) : "",
    quantity: v.quantity ?? 0,
    reserved: v.reserved ?? 0,
    incoming: v.incoming ?? 0,
  }));

  const inventory = {
    lowStockThreshold:
      prod.low_stock_threshold != null ? prod.low_stock_threshold : "",
  };

  const media = {
    productImages: (prod.images || []).map((img) => img.url).filter(Boolean),
  };

  return {
    draftId: prod.id || null,
    basic,
    options,
    variants,
    inventory,
    media,
    threeD: {
      variants: (prod.variant3d || []).map((entry) => ({
        variantKey: entry.variantKey,
        variantId: entry.variantId || null,
        status: entry.status || null,
        modelAssetId: entry.originalAssetId || null,
        optimizedAssetId: entry.optimizedAssetId || null,
        posterAssetId: entry.posterAssetId || null,
        originalUrl: entry.originalUrl || null,
        previewUrl: entry.previewUrl || null,
        posterUrl: entry.posterUrl || null,
        viewerConfig: entry.viewerConfig || null,
        processingReport: entry.processingReport || null,
        warnings: entry.warnings || [],
        lastProcessedAt: entry.lastProcessedAt || null,
        archivedAt: entry.archivedAt || null,
      })),
    },
  };
}

/* ────── normalize state → API payload ────── */

export function normalizePayload(state, updatedAt) {
  const basic = {
    name: String(state.basic?.name || "").trim(),
    category: String(state.basic?.category || "").trim(),
    tags: Array.isArray(state.basic?.tags)
      ? [...new Set(state.basic.tags.map((tagId) => String(tagId || "").trim()).filter(Boolean))]
      : [],
    description: String(state.basic?.description || "").trim(),
    skuBase: String(state.basic?.skuBase || "").trim(),
  };

  const options = (state.options || []).map((o) => ({
    name: String(o?.name || "").trim(),
    values: (o?.values || []).map((v) => String(v || "").trim()).filter(Boolean),
  }));

  const variants = (state.variants || []).map((v) => {
    const priceRaw = v?.price;
    let price;
    if (priceRaw === "" || priceRaw == null) price = "";
    else {
      const priceNum = Number(priceRaw);
      price = Number.isFinite(priceNum) ? priceNum : "";
    }
    return {
      optionValues: v?.optionValues || {},
      sku: String(v?.sku || "").trim(),
      imageUrl: v?.imageUrl ?? null,
      isActive: v?.isActive !== false,
      price,
    };
  });

  const inv = state.inventory || {};
  const lowStockRaw = inv.lowStockThreshold;
  let lowStockVal = "";
  if (lowStockRaw !== "" && lowStockRaw != null) {
    const n = Number(lowStockRaw);
    lowStockVal = Number.isFinite(n) && n >= 0 ? Math.floor(n) : "";
  }
  const inventory = {
    lowStockThreshold: lowStockVal,
  };

  const media = {
    productImages: Array.isArray(state.media?.productImages) ? state.media.productImages : [],
  };

  return { basic, options, variants, inventory, media, updatedAt: updatedAt || undefined };
}

/* ══════════════════════════════════════════════════════════════
   BusinessEditProduct
   ══════════════════════════════════════════════════════════════ */

export default function BusinessEditProduct() {
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialStep = stepIndexFromParam(searchParams.get("step"));

  const [stepIndex, setStepIndex] = useState(initialStep);
  const [maxVisitedIndex, setMaxVisitedIndex] = useState(STEPS.length - 1); // all steps accessible in edit mode

  const [state, setState] = useState(null); // null = not loaded yet
  const [productStatus, setProductStatus] = useState("DRAFT");
  const [updatedAt, setUpdatedAt] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveWarnings, setSaveWarnings] = useState([]);
  const [listingIssues, setListingIssues] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);

  const dirtyRef = useRef(false);
  const lastSavedPayloadRef = useRef(null);

  const swalBase = useMemo(
    () => ({ ...businessSwalBase, target: "#business-swal-root" }),
    [],
  );

  /* ── Load product ── */

  const loadProduct = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await businessProductsAPI.getById(productId);
      const prod = res?.data || res;
      if (!prod || !prod.id) throw new Error("Product not found");

      const ws = productToState(prod);
      setState(ws);
      setProductStatus((prod.status || "DRAFT").toUpperCase());
      setUpdatedAt(prod.updated_at || null);
      lastSavedPayloadRef.current = normalizePayload(ws, prod.updated_at || null);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to load product.";
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  /* ── Payload (memoised) ── */

  const payload = useMemo(
    () => (state ? normalizePayload(state, updatedAt) : null),
    [state, updatedAt],
  );

  /* ── Step navigation ── */

  const setUrlStep = useCallback(
    (idx, opts = {}) => {
      const key = STEPS[idx]?.key || STEPS[0].key;
      const next = new URLSearchParams(searchParams);
      next.set("step", key);
      setSearchParams(next, { replace: !!opts.replace });
    },
    [searchParams, setSearchParams],
  );

  const goToStep = useCallback(
    (idx) => {
      const desired = clamp(idx, 0, STEPS.length - 1);
      if (desired > maxVisitedIndex) return;
      setStepIndex(desired);
      setUrlStep(desired);
    },
    [maxVisitedIndex, setUrlStep],
  );

  // Sync browser back/forward
  useEffect(() => {
    const urlIdx = stepIndexFromParam(searchParams.get("step"));
    if (urlIdx !== stepIndex && urlIdx <= maxVisitedIndex) setStepIndex(urlIdx);
  }, [searchParams, stepIndex, maxVisitedIndex]);

  // Ensure URL has step param
  useEffect(() => {
    if (!searchParams.get("step")) setUrlStep(stepIndex, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Dirty tracking ── */

  const setWizardState = useCallback((updater) => {
    setState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      dirtyRef.current = true;
      return next;
    });
  }, []);

  /* ── Autosave is intentionally DISABLED on the edit page. ── */
  /* Changes are persisted only via the manual Save button.    */

  /* ── Prevent accidental close ── */

  useEffect(() => {
    const handler = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  /* ── Manual save ── */

  const performManualSave = useCallback(async () => {
    if (!state || !productId) return false;
    setSaving(true);
    setSaveError("");
    try {
      const res = await businessProductsAPI.update(productId, payload);
      const newUpdatedAt = res?.data?.updatedAt || res?.updatedAt || null;
      if (newUpdatedAt) setUpdatedAt(newUpdatedAt);
      setLastSavedAt(new Date().toISOString());
      dirtyRef.current = false;
      lastSavedPayloadRef.current = payload;

      const warnings = res?.data?.warnings || res?.warnings || [];
      setSaveWarnings(Array.isArray(warnings) ? warnings : []);

      // Surface listing-readiness issues for listed products
      const issues = res?.data?.listingIssues || res?.listingIssues || [];
      setListingIssues(issues);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed.";
      setSaveWarnings([]);
      setSaveError(msg);
      return false;
    } finally {
      setSaving(false);
    }
  }, [payload, productId, state]);

  const handleManualSave = useCallback(async (opts = {}) => {
    const skipConfirm = !!opts.skipConfirm;
    if (!state || !productId) return false;

    // Check for missing variant combos
    const missing = computeMissingCombos(payload?.options, payload?.variants);

    if (!skipConfirm) {
      const summary = buildSaveSummary(lastSavedPayloadRef.current, payload);

      if (summary.length === 0 && missing.length === 0) {
        await Swal.fire({
          ...swalBase,
          icon: "info",
          title: "No changes detected",
          html: "There are no new changes to save.",
          confirmButtonText: "OK",
        });
        return false;
      }

      // Build the confirmation body
      let bodyHtml = "";

      if (summary.length > 0) {
        bodyHtml += `<div class="text-left"><p class="mb-2 font-medium">Changes to save:</p><ul class="list-disc pl-5 space-y-1 text-sm">${summary.map((s) => `<li>${s}</li>`).join("")}</ul></div>`;
      }

      if (missing.length > 0) {
        bodyHtml += `<div class="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-left text-sm dark:border-amber-800 dark:bg-amber-950/40">`;
        bodyHtml += `<p class="font-semibold text-amber-800 dark:text-amber-200">⚠ ${missing.length} missing variant${missing.length > 1 ? "s" : ""}</p>`;
        bodyHtml += `<p class="mt-1 text-xs text-amber-700 dark:text-amber-300">Some option combinations don't have matching variants yet. Open the Variants step to let the matrix auto-generate rows from the latest options.</p>`;
        bodyHtml += `</div>`;
      }

      const result = await Swal.fire({
        ...swalBase,
        icon: missing.length > 0 ? "warning" : "question",
        title: missing.length > 0 ? "Save with incomplete variants?" : "Save changes?",
        html: bodyHtml,
        showCancelButton: true,
        confirmButtonText: missing.length > 0 ? "Save Anyway" : "Confirm Save",
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return false;
    }

    return await performManualSave();
  }, [payload, performManualSave, productId, state, swalBase]);

  const confirmStepLeaveIfDirty = useCallback(async () => {
    if (!dirtyRef.current) return true;

    const result = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "You have unsaved changes",
      html:
        '<p class="mb-1">Leaving now will lose any edits you\'ve made.</p>' +
        '<p class="text-xs text-neutral-500 dark:text-neutral-400">Save your changes first, or discard them to continue.</p>',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Save Changes",
      denyButtonText: "Discard",
      cancelButtonText: "Stay Here",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      const ok = await handleManualSave({ skipConfirm: true });
      return !!ok;
    }
    if (result.isDenied) {
      dirtyRef.current = false;
      return true;
    }
    return false;
  }, [handleManualSave, swalBase]);

  const goNext = useCallback(() => {
    const next = clamp(stepIndex + 1, 0, STEPS.length - 1);
    setMaxVisitedIndex((m) => Math.max(m, next));
    setStepIndex(next);
    setUrlStep(next);

    window.requestAnimationFrame(() => {
      const content = document.querySelector(".business-content");
      if (content instanceof HTMLElement) {
        content.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  }, [setUrlStep, stepIndex]);

  const goBack = useCallback(async () => {
    const canLeave = await confirmStepLeaveIfDirty();
    if (!canLeave) return;
    goToStep(stepIndex - 1);
  }, [confirmStepLeaveIfDirty, goToStep, stepIndex]);

  const goBackToProducts = useCallback(async () => {
    const canLeave = await confirmStepLeaveIfDirty();
    if (!canLeave) return;
    navigate("/business/products");
  }, [confirmStepLeaveIfDirty, navigate]);

  // BrowserRouter-safe guard for sidebar/top navigation links.
  useEffect(() => {
    const onDocumentClick = async (e) => {
      if (!dirtyRef.current) return;
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = e.target?.closest?.("a[href]");
      if (!anchor) return;
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;

      const nextUrl = new URL(href, window.location.origin);
      if (nextUrl.origin !== window.location.origin) return;

      const current = `${window.location.pathname}${window.location.search}`;
      const next = `${nextUrl.pathname}${nextUrl.search}`;
      if (next === current) return;

      e.preventDefault();
      const canLeave = await confirmStepLeaveIfDirty();
      if (!canLeave) return;
      navigate(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    };

    return () => document.removeEventListener("click", onDocumentClick, true);
  }, [confirmStepLeaveIfDirty, navigate]);

  /* ── List for Sale ── */

  const handlePublish = useCallback(async () => {
    // Block listing if variant matrix is incomplete
    const missing = computeMissingCombos(payload?.options, payload?.variants);
    if (missing.length > 0) {
      await Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Cannot list for sale",
        html:
          `<p class="mb-2">${missing.length} variant combination${missing.length > 1 ? "s are" : " is"} missing.</p>` +
          `<p class="text-xs text-neutral-500 dark:text-neutral-400">Go to the <b>Variants</b> step. Rows are generated automatically from current options, then complete any missing SKU/price details before listing.</p>`,
        confirmButtonText: "OK",
      });
      return;
    }

    const result = await Swal.fire({
      ...swalBase,
      icon: "question",
      title: "List product for sale?",
      html: "This will make the product visible to customers. Are you sure?",
      showCancelButton: true,
      confirmButtonText: "List for Sale",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setStatusBusy(true);
    try {
      // Save any pending changes first
      if (dirtyRef.current) {
        await businessProductsAPI.update(productId, payload);
        dirtyRef.current = false;
      }

      const res = await businessProductsAPI.publishDraft(productId, { status: "LISTED" });
      const newStatus = res?.data?.status || res?.status || "LISTED";
      const newUpdatedAt = res?.data?.updatedAt || res?.updatedAt || null;
      setProductStatus(newStatus.toUpperCase());
      setListingIssues([]);
      if (newUpdatedAt) setUpdatedAt(newUpdatedAt);
      setLastSavedAt(new Date().toISOString());

      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Listed!",
        html: "Your product is now listed for sale.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Listing failed.";
      const details = e?.response?.data?.details;
      await Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Cannot list for sale",
        html: details ? `<ul class="text-left text-sm list-disc pl-4">${details.map((d) => `<li>${d}</li>`).join("")}</ul>` : msg,
        confirmButtonText: "OK",
      });
    } finally {
      setStatusBusy(false);
    }
  }, [productId, payload, swalBase]);

  /* ── Unlist (→ Draft) ── */

  const handleUnpublish = useCallback(async () => {
    const result = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "Unlist product?",
      html: "This will remove the product from your storefront. Customers will no longer see it.",
      showCancelButton: true,
      confirmButtonText: "Unlist",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setStatusBusy(true);
    try {
      const res = await businessProductsAPI.updateStatus(productId, "DRAFT");
      const newStatus = res?.data?.status || res?.status || "DRAFT";
      const newUpdatedAt = res?.data?.updatedAt || res?.updatedAt || null;
      setProductStatus(newStatus.toUpperCase());
      setListingIssues([]);
      if (newUpdatedAt) setUpdatedAt(newUpdatedAt);
      setLastSavedAt(new Date().toISOString());

      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Unlisted",
        html: "Product removed from your storefront.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Unlist failed.";
      await Swal.fire({ ...swalBase, icon: "error", title: "Error", html: msg, confirmButtonText: "OK" });
    } finally {
      setStatusBusy(false);
    }
  }, [productId, swalBase]);

  /* ── Delete product ── */

  const handleDelete = useCallback(async () => {
    const productName = state?.basic?.name || "this product";
    const result = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "Delete product?",
      html: `<p>This will permanently remove <strong>${productName}</strong> from your store.</p><p class="mt-2 text-xs text-neutral-500">This action cannot be undone.</p>`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setStatusBusy(true);
    try {
      await businessProductsAPI.remove(productId);
      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Deleted",
        html: "Product has been removed.",
        timer: 1800,
        showConfirmButton: false,
      });
      dirtyRef.current = false;
      navigate("/business/products");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Delete failed.";
      await Swal.fire({ ...swalBase, icon: "error", title: "Error", html: msg, confirmButtonText: "OK" });
    } finally {
      setStatusBusy(false);
    }
  }, [productId, state, swalBase, navigate]);

  /* ── Render: loading / error states ── */

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (loadError || !state) {
    return (
      <div className="space-y-4" id="business-swal-root">
        <Panel eyebrow="Merchandising" title="Edit Product">
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200">
            {loadError || "Product not found."}
          </div>
          <button
            type="button"
            onClick={goBackToProducts}
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-mnla-border px-4 py-2 text-sm font-medium text-mnla-text hover:bg-neutral-50 dark:border-mnla-darkBorder dark:text-mnla-darkText dark:hover:bg-neutral-900/60"
          >
            <ArrowLeft size={14} /> Back to products
          </button>
        </Panel>
      </div>
    );
  }

  /* ── Render: main edit form ── */

  const Step = STEPS[stepIndex]?.key;
  const isLastStep = stepIndex === STEPS.length - 1;

  // Step-level validation for WizardHeader badges
  const stepStates = (() => {
    if (!state) return [];
    const p = normalizePayload(state, updatedAt);
    return STEPS.map((s, idx) => {
      let issues = [];
      switch (s.key) {
        case "basic":
          if (!p.basic.name) issues.push("Product name is required.");
          if (!p.basic.category) issues.push("Category is required.");
          break;
        case "options": {
          const opts = p.options.filter((o) => o.name && o.values.length);
          if (!opts.length) issues.push("Add at least one option.");
          break;
        }
        case "variants":
          if (!p.variants.length) issues.push("Generate variants first.");
          break;
        case "media":
          if (!p.media.productImages.length) issues.push("Add at least 1 image.");
          break;
        case "threeD":
          issues = [];
          break;
        default:
          break;
      }
      const ok = issues.length === 0;
      // Current step: "current" if ok, "blocked" if has issues
      if (idx === stepIndex) return ok ? { status: "current" } : { status: "blocked", hint: issues[0] };
      // All other steps: data-driven evaluation — "done" or "blocked"
      return ok ? { status: "done" } : { status: "blocked", hint: issues[0] };
    });
  })();

  return (
    <div className="space-y-4" id="business-swal-root">
      <Panel
        eyebrow="Merchandising"
        title={
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3 min-w-0">
            <button
              type="button"
              onClick={goBackToProducts}
              className="inline-flex items-center gap-1 shrink-0 text-sm text-mnla-muted hover:text-mnla-text dark:text-mnla-darkMuted dark:hover:text-mnla-darkText"
            >
              <ArrowLeft size={14} /> Products
            </button>
            <span
              className="text-base font-semibold text-mnla-text dark:text-mnla-darkText truncate min-w-0"
              title={state.basic?.name || "Untitled product"}
            >
              {state.basic?.name || "Untitled product"}
            </span>
            <StatusBadge status={productStatus} />
          </div>
        }
        right={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {/* Last saved indicator */}
            {lastSavedAt && (
              <span className="text-xs text-mnla-muted dark:text-mnla-darkMuted whitespace-nowrap">
                Saved {relativeTime(lastSavedAt)}
              </span>
            )}

            {/* Save — muted outline style, clearly secondary */}
            <button
              type="button"
              onClick={handleManualSave}
              disabled={saving || statusBusy}
              title="Save changes"
              aria-label="Save"
              className="inline-flex items-center gap-1.5 rounded-xl border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 shadow-sm hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {saving ? "Saving…" : "Save"}
            </button>

            {/* List for Sale or Unlist — visually dominant */}
            {productStatus === "LISTED" ? (
              <button
                type="button"
                onClick={handleUnpublish}
                disabled={saving || statusBusy}
                title="Remove from your storefront — customers will no longer see this product"
                className="inline-flex items-center gap-1.5 rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-2 text-sm font-bold text-amber-800 shadow-sm hover:bg-amber-100 disabled:opacity-50 dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-200 dark:hover:bg-amber-900/60"
              >
                {statusBusy ? <Loader2 size={14} className="animate-spin" /> : null}
                Unlist
              </button>
            ) : (
              <button
                type="button"
                onClick={handlePublish}
                disabled={saving || statusBusy}
                title="Make this product live — customers will be able to see and buy it"
                className="business-primary-action inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-md hover:bg-emerald-700 disabled:opacity-50 dark:bg-emerald-600 dark:hover:bg-emerald-500"
              >
                {statusBusy ? <Loader2 size={14} className="animate-spin" /> : null}
                List for Sale
              </button>
            )}

            {/* Delete — danger, small, clearly destructive */}
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving || statusBusy}
              title="Permanently delete this product"
              className="inline-flex items-center gap-1 rounded-xl border border-rose-200 px-2.5 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900 dark:text-rose-400 dark:hover:bg-rose-950/40"
            >
              <Trash2 size={13} />
            </button>
          </div>
        }
      >
        <WizardHeader
          title="Edit Product"
          steps={STEPS}
          stepIndex={stepIndex}
          stepStates={stepStates}
          saving={saving}
          error={saveError}
          warnings={saveWarnings}
          lastSavedAt={lastSavedAt}
          onStepClick={goToStep}
          maxClickableIndex={maxVisitedIndex}
          contained
        />

        <div className="mt-4 space-y-4">
          {/* Listing issues warning */}
          {listingIssues.length > 0 && productStatus === "LISTED" && (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              <div className="font-semibold">This product is listed but has issues</div>
              <div className="mt-2 space-y-1">
                {listingIssues.map((issue) => (
                  <div key={issue}>• {issue}</div>
                ))}
              </div>
            </div>
          )}

          {/* A saved product is not a first-time form: a cleared name or category is flagged at once, as before. */}
          {Step === "basic" && <StepBasicInfo state={state} setState={setWizardState} showRequiredErrors />}
          {Step === "options" && <StepOptions state={state} setState={setWizardState} />}
          {Step === "variants" && (
            <StepVariants
              state={state}
              setState={setWizardState}
              saveWarnings={saveWarnings}
            />
          )}
          {Step === "inventory" && <StepInventory state={state} setState={setWizardState} />}
          {Step === "media" && <StepMedia state={state} setState={setWizardState} />}
          {Step === "threeD" && (
            <Step3DAssetsViewer
              state={state}
              setState={setWizardState}
            />
          )}

          {/* Navigation */}
          <div
            className={
              isLastStep
                ? "flex flex-col gap-2 rounded-2xl border border-zinc-200 bg-zinc-50 p-3 sm:flex-row sm:justify-between dark:border-zinc-800 dark:bg-[#121214]"
                : "sticky bottom-0 z-20 flex flex-col gap-2 rounded-2xl border border-zinc-200/80 bg-white/95 p-3 shadow-[0_-6px_20px_rgba(15,23,42,0.08)] backdrop-blur sm:flex-row sm:justify-between dark:border-zinc-800 dark:bg-[#121214]/95"
            }
          >
            <button
              type="button"
              onClick={goBack}
              disabled={stepIndex === 0}
              className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm font-medium text-zinc-900 disabled:opacity-40 sm:w-auto dark:border-zinc-800 dark:bg-[#121214] dark:text-zinc-100"
            >
              Back
            </button>
            {stepIndex < STEPS.length - 1 && (
              <button
                type="button"
                onClick={goNext}
                className="w-full rounded-xl bg-zinc-900 px-4 py-3 text-sm font-medium text-white sm:w-auto dark:bg-zinc-100 dark:text-zinc-900"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </Panel>
    </div>
  );
}
